import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/Status";
import OnboardingWizard from "@/components/OnboardingWizard";
import { ORDER, CREST } from "@/constants/testIds";

const DASH_HERO =
  "/brand/proshop-bags-clubs.jpg";

const ONB_DISMISS_KEY = "mcc.onb.dismissed";

export default function MemberDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    typeof window !== "undefined" && window.sessionStorage.getItem(ONB_DISMISS_KEY) === "1",
  );
  const [options, setOptions] = useState({ styles: [], brands: [], categories: [] });

  useEffect(() => {
    api
      .get("/orders")
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
    api.get("/auth/me/preferences/options").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  const needsOnboarding = user && !user.onboarding_completed_at;
  const showBanner = needsOnboarding && !bannerDismissed;

  const dismissBanner = () => {
    setBannerDismissed(true);
    try {
      window.sessionStorage.setItem(ONB_DISMISS_KEY, "1");
    } catch (err) {
      // Storage disabled (private mode) — banner still dismisses for the session via state.
      console.warn("Could not persist onboarding-banner dismissal:", err?.message || err);
    }
  };

  // Active = anything that's not a terminal state (closed = picked up, cancelled = scrapped).
  const active = orders.filter((o) => !["ready", "closed", "cancelled"].includes(o.status));
  const recent = orders.slice(0, 4);

  return (
    <div className="bg-cream min-h-screen">
      {/* Onboarding banner */}
      {showBanner && (
        <div
          data-testid={CREST.banner}
          className="bg-[#1A422D] text-[#F9F8F4]"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>Welcome to the Crest Club</div>
              <p className="text-sm mt-1">
                Take a minute to build your <span className="font-serif italic">Crest Profile</span> — your sizes, styles, and favorite brands. It helps us source the right things for you.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setWizardOpen(true)}
                data-testid={CREST.bannerStart}
                className="px-4 py-2 text-xs uppercase tracking-wider bg-[#D9C68A] text-[#1A422D] hover:bg-[#E5D6A8] transition-colors"
              >
                Get Started
              </button>
              <button
                type="button"
                onClick={dismissBanner}
                aria-label="Dismiss"
                data-testid={CREST.bannerDismiss}
                className="text-[#D9C68A] hover:text-[#F9F8F4] text-xl leading-none px-2"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E3D8]">
        <div className="absolute inset-0">
          <img src={DASH_HERO} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F9F8F4] via-[#F9F8F4]/85 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
          <div className="overline">Members&rsquo; Portal</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 text-[#1A1A1A] leading-none max-w-2xl">
            Good day, {user?.name?.split(" ")[0] || "Member"}.
          </h1>
          <p className="mt-4 max-w-xl text-[#4A4A4A]">
            Submit a special order and the pro shop will place it with the vendor on your
            behalf. Photos, colour, size, and logo preference — all handled with discretion.
          </p>
          <div className="mt-8 flex gap-4">
            <Link to="/orders/new" className="btn-primary">Place a Special Order</Link>
            <Link to="/orders" className="btn-secondary">View History</Link>
          </div>
        </div>
      </section>

      {/* Active orders */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline">In Progress</div>
            <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Active Orders</h2>
          </div>
          <Link to="/orders" className="text-sm uppercase tracking-wider text-[#1A422D] hover:underline">
            All orders →
          </Link>
        </div>

        {loading ? (
          <div className="overline text-[#7a7a7a]">Loading…</div>
        ) : active.length === 0 ? (
          <div className="surface-card p-10 text-center">
            <div className="overline">Nothing in motion</div>
            <p className="font-serif text-2xl mt-2 text-[#1A1A1A]">
              You have no active orders.
            </p>
            <p className="text-[#4A4A4A] mt-2">Place your first special order to get started.</p>
            <Link to="/orders/new" className="btn-primary inline-block mt-6">New Order</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {active.map((o) => (
              <Link
                key={o.order_id}
                to={`/orders/${o.order_id}`}
                data-testid={`${ORDER.cardPrefix}${o.order_id}`}
                className="surface-card p-6 hover:shadow-[0_8px_30px_rgba(26,66,45,0.08)] transition-shadow duration-200 block"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="overline">{o.category || "Special Order"}</div>
                    <div className="font-serif text-xl mt-1 text-[#1A1A1A]">{o.item_name}</div>
                  </div>
                  <StatusPill status={o.status} />
                </div>
                <div className="mt-4 text-sm text-[#4A4A4A] grid grid-cols-2 gap-x-4 gap-y-1">
                  {o.color && (<><span className="label-elegant">Color</span><span>{o.color}</span></>)}
                  {o.size && (<><span className="label-elegant">Size</span><span>{o.size}</span></>)}
                  {o.quantity && (<><span className="label-elegant">Qty</span><span>{o.quantity}</span></>)}
                </div>
                <div className="mt-4 text-xs text-[#7A7A7A]">
                  Submitted {new Date(o.created_at).toLocaleDateString()}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Recent activity */}
      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16">
          <div className="overline">Recent Activity</div>
          <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Latest Submissions</h2>
          <div className="mt-6 surface-card divide-y divide-[#E5E3D8]">
            {recent.map((o) => (
              <Link
                key={o.order_id}
                to={`/orders/${o.order_id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-[#F3F1E9] transition-colors"
              >
                <div>
                  <div className="font-serif text-lg text-[#1A1A1A]">{o.item_name}</div>
                  <div className="text-xs text-[#7A7A7A] uppercase tracking-wider">
                    {new Date(o.created_at).toLocaleDateString()} · {o.quantity} unit{o.quantity > 1 ? "s" : ""}
                  </div>
                </div>
                <StatusPill status={o.status} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* My Clubs — multi-tenant member memberships */}
      <MyClubsSection />

      <OnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        options={options}
      />
    </div>
  );
}

function MyClubsSection() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/auth/me/clubs")
      .then((r) => setClubs(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Hide the section entirely if the member only belongs to one club AND that
  // membership is active — it'd be redundant clutter for the default case.
  if (loading) return null;
  const hasPending = clubs.some((c) => c.membership_status === "pending");
  if (clubs.length <= 1 && !hasPending) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16" data-testid="my-clubs-section">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline">Your Membership</div>
          <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">My Clubs</h2>
        </div>
        <span className="text-xs text-[#7A7A7A]">
          One account · {clubs.filter((c) => c.membership_status === "active").length} active · {clubs.filter((c) => c.membership_status === "pending").length} pending
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {clubs.map((c, idx) => (
          <div
            key={c.club_id || c.slug || `legacy-${idx}`}
            data-testid={`my-clubs-card-${c.slug || c.club_id || `legacy-${idx}`}`}
            className="surface-card p-6 flex flex-col"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="overline">{c.city || "Private Club"}</div>
                <div className="font-serif text-xl mt-1 text-[#1A1A1A]">{c.name}</div>
              </div>
              <MembershipPill status={c.membership_status} />
            </div>
            <div className="mt-3 text-xs text-[#7A7A7A]">
              {c.is_home && <span className="mr-2 px-2 py-0.5 border border-[#B59A5A] text-[#8a6f33]">Home Club</span>}
              {c.member_number && <span className="font-mono">#{c.member_number}</span>}
            </div>
            <div className="mt-auto pt-4">
              {c.membership_status === "pending" ? (
                <span className="text-xs text-[#7A7A7A]">Awaiting approval from the pro shop.</span>
              ) : (
                <Link to="/orders/new" className="text-xs uppercase tracking-[0.18em] text-[#1A422D] hover:underline">
                  Submit a request →
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MembershipPill({ status }) {
  if (status === "pending") {
    return <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33]">Pending</span>;
  }
  return <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]">Active</span>;
}
